import cx from 'classnames';
import * as React from 'react';
import { useState, Children, cloneElement, KeyboardEvent, ReactElement } from 'react';
import Button from '../../../button/button';
import { FlosInput } from '../../input/flos-input';

type DropdownProps = {
    defaultLabel?: string;
    children?: ReactElement<DropdownItemProps> | ReactElement<DropdownItemProps>[];
    disabled?: boolean;
    value?: string;
    onChange?: any;
} & Omit<React.ComponentPropsWithRef<'button'>, 'onChange'>;

type DropdownItemProps = {
    /**
     * Value of the drop down item
     **/
    value?: string;
    selected?: boolean;
    children?: React.ReactNode;
    onClick?: React.MouseEventHandler<HTMLLIElement>;
    onKeyDown?: React.KeyboardEventHandler<HTMLLIElement>;
};

const Dropdown = React.forwardRef<HTMLButtonElement, DropdownProps>(
    ({ defaultLabel, children, onChange, className, ...props }: DropdownProps, ref): React.ReactElement => {
        const arrayChildren = Children.toArray(children) as any;
        const initialOption = arrayChildren.findIndex((child: any) => child.props.selected);
        const [showOptions, setShowOptions] = useState<boolean>(false);
        const [selectedOption, setSelectedOption] = useState<number | null>(initialOption >= 0 ? initialOption : null);
        const [inputValue, setInputValue] = React.useState('');
        const inputRef = (ref as React.MutableRefObject<HTMLInputElement>) || (React.useRef() as React.MutableRefObject<HTMLInputElement>);
        const listRef = React.useRef() as React.MutableRefObject<HTMLUListElement>;

        const toggleOptions = () => {
            setShowOptions(!showOptions);
        };

        const setAndClose = (index: number) => {
            setSelectedOption(index);
            setShowOptions(false);
        };

        const handleKeyDown = (index: number) => (e: KeyboardEvent) => {
            switch (e.key) {
                case ' ':
                case 'SpaceBar':
                case 'Enter':
                    e.preventDefault();
                    setAndClose(index);
                    break;
                default:
                    break;
            }
        };

        const handleListKeyDown = (e: KeyboardEvent) => {
            const currentOption = typeof selectedOption == 'number' ? selectedOption : -1;
            switch (e.key) {
                case 'Escape':
                    e.preventDefault();
                    setShowOptions(false);
                    break;
                case 'ArrowUp':
                    e.preventDefault();
                    currentOption - 1 >= 0 && setSelectedOption(currentOption - 1);
                    break;
                case 'ArrowDown':
                    e.preventDefault();
                    currentOption + 1 < arrayChildren.length && setSelectedOption(currentOption + 1);
                    break;
                default:
                    break;
            }
        };

        React.useEffect(() => {
            for (let i = 0; i < arrayChildren.length; i++) {
                if (arrayChildren[i].props.selected) {
                    setSelectedOption(i);
                }
            }
        }, []);

        React.useEffect(() => {
            if (typeof selectedOption == 'number') {
                setInputValue(arrayChildren[selectedOption].props.value);
            }
            const selected = listRef.current.querySelector('li[aria-selected=true]') as HTMLElement;
            if (selected && !isInParentsViewPort(selected) && typeof selected.scrollIntoView == 'function') {
                selected?.scrollIntoView();
            }
        }, [selectedOption]);

        React.useEffect(() => {
            onChange && onChange({ target: inputRef.current, currentTarget: inputRef.current });
        }, [inputValue]);

        React.useEffect(() => {
            const handleClickOutside = (e: MouseEvent) => {
                if (showOptions) {
                    if (!listRef.current.contains(e.target as Node)) {
                        setShowOptions(false);
                    }
                    return;
                }
            };
            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }, [showOptions]);

        return (
            <>
                <div className={cx('flos-dropdown', className)}>
                    <FlosInput autoComplete={'off'} type="hidden" value={inputValue} ref={inputRef} />
                    <Button
                        className="flos-button--icon"
                        type="button"
                        aria-haspopup="listbox"
                        aria-expanded={showOptions}
                        variant="secondary"
                        onClick={toggleOptions}
                        onKeyDown={handleListKeyDown}
                        {...props}
                    >
                        {defaultLabel && selectedOption == null ? defaultLabel : arrayChildren[selectedOption !== null ? selectedOption : 0].props.children}
                        <flos-icon size={16} shape="arrow-down" class="is-logo-blue" />
                    </Button>
                    <div className={'flos-dropdown-outer'}>
                        <div className={cx('flos-dropdown-inner', showOptions && 'show')}>
                            <ul role={'listbox'} className={cx('flos-dropdown-list', 'u-scrollbar')} ref={listRef}>
                                {Children.map(children, (child: any, index: number) => {
                                    return cloneElement(child as any, {
                                        selected: selectedOption == index,
                                        onClick: () => {
                                            setAndClose(index);
                                        },
                                        onKeyDown: handleKeyDown(index),
                                    });
                                })}
                            </ul>
                        </div>
                    </div>
                </div>
            </>
        );
    }
);

const DropdownItem = ({ value, selected, onClick, onKeyDown, children, ...props }: DropdownItemProps): React.ReactElement => {
    return (
        <li className={'flos-dropdown-list-item'} role={'option'} tabIndex={0} value={value} aria-selected={selected} onClick={onClick} {...props}>
            {children}
        </li>
    );
};

const isInParentsViewPort = (element: HTMLElement) => {
    const parent = element?.offsetParent;
    if (parent) {
        const elemBounding = element.getBoundingClientRect();
        const parentBounding = parent.getBoundingClientRect();
        return elemBounding.bottom <= parentBounding.height + parentBounding.top && elemBounding.top >= parentBounding.top;
    }
    return false;
};

export type { DropdownProps, DropdownItemProps };
export { Dropdown, DropdownItem };
export default Dropdown;
